import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import {
  Button,
  Row,
  Col,
  Input,
  Badge,
  Card,
  CardBody,
  Alert,
  CardHeader,
} from "reactstrap";
import { autoLabelGroups } from "../../../utils/groupUtils";
import { RiInformationLine } from "react-icons/ri";

const GroupsManager = ({
  groups,
  allFiles = [], // ALL files from the project, not filtered
  filteredFiles = [], // Files filtered by seq type/group for selection
  onAddGroup,
  onRemoveGroup,
  onUpdateGroupName,
  onUpdateGroupColor,
  onAddFilesToGroup,
  onRemoveFilesFromGroup,
  onClearGroup,
  onAutoFill,
  onAutoLabel,
  onClearAll,
}) => {
  const [draggedFile, setDraggedFile] = useState(null);
  const [draggedFileSourceGroup, setDraggedFileSourceGroup] = useState(null);
  const [dragOverGroup, setDragOverGroup] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [lastSelectedFileIndex, setLastSelectedFileIndex] = useState(null);
  const availableFilesRef = useRef([]);

  const [tooltipVisible, setTooltipVisible] = useState({});

  // Memoize available files to prevent unnecessary recalculations
  const availableFiles = useMemo(() => {
    const available = allFiles.filter(
      (file) => !groups.some((g) => g.files.includes(allFiles.file_id))
    );
    availableFilesRef.current = available;
    return available;
  }, [allFiles, groups]);

  const minCellWidth = useMemo(() => {
    if (availableFiles.length === 0) return 180;
      const longestName = Math.max(...availableFiles.map(f => f.file_name.length));
      const calculated = longestName * 7 + 40;
    return Math.min(Math.max(calculated, 180), 280);
  }, [availableFiles]);
    useEffect(() => {
      if (Array.isArray(allFiles) && allFiles.length > 0) {
        window.globalFileCache = allFiles;
      }
  }, [allFiles]);

  // Handle drag start from file selection area (unassigned files)
  const handleDragStart = useCallback(
    (e, fileId) => {
      // If dragging a selected file, drag all selected files
      if (selectedFiles.includes(fileId)) {
        setDraggedFile(selectedFiles);
      } else {
        setDraggedFile([fileId]);
      }
      setDraggedFileSourceGroup(null); // Files from selection area have no source group
      e.dataTransfer.effectAllowed = "move";
    },
    [selectedFiles]
  );

  // Handle drag start from files within groups (assigned files)
  const handleGroupFileDragStart = useCallback((e, fileId, sourceGroupId) => {
    e.stopPropagation(); // Prevent event bubbling
    setDraggedFile([fileId]);
    setDraggedFileSourceGroup(sourceGroupId);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback((e, groupId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverGroup(groupId);
  }, []);

  const handleDragLeave = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverGroup(null);
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedFile(null);
    setDraggedFileSourceGroup(null);
    setDragOverGroup(null);
  }, []);

  const handleTooltipShow = useCallback((groupId) => {
    setTooltipVisible((prev) => ({ ...prev, [groupId]: true }));
  }, []);

  const handleTooltipHide = useCallback((groupId) => {
    setTooltipVisible((prev) => ({ ...prev, [groupId]: false }));
  }, []);

  const handleDrop = useCallback(
    (e, targetGroupId) => {
      e.preventDefault();
      if (draggedFile) {
        // Handle both single file and multiple files
        const filesToMove = Array.isArray(draggedFile)
          ? draggedFile
          : [draggedFile];

        if (draggedFileSourceGroup) {
          // Moving files from one group to another
          if (draggedFileSourceGroup !== targetGroupId) {
            // Remove files from source group
            onRemoveFilesFromGroup(draggedFileSourceGroup, filesToMove);
            // Add files to target group
            onAddFilesToGroup(targetGroupId, filesToMove);
          }
          // If dropping on the same group, do nothing (no visual change needed)
        } else {
          // Adding files from selection area to group
          onAddFilesToGroup(targetGroupId, filesToMove);
          // Clear selection after successful drop
          setSelectedFiles([]);
          setLastSelectedFileIndex(null);
        }

        setDraggedFile(null);
        setDraggedFileSourceGroup(null);
      }
      setDragOverGroup(null);
    },
    [
      draggedFile,
      draggedFileSourceGroup,
      onAddFilesToGroup,
      onRemoveFilesFromGroup,
    ]
  );

  // Enhanced file click handler with range selection support
  const handleFileClick = useCallback(
    (e, fileId, fileIndex) => {
      e.preventDefault();

      // Check if file is already assigned
      const isAssigned = groups.some((g) => g.files.includes(fileId));
      if (isAssigned) return;

      if (e.shiftKey && lastSelectedFileIndex !== null) {
        // Shift click - select range (enhanced)
        const availableFiles = filteredFiles.filter(
          (file) => !groups.some((g) => g.files.includes(file.file_id))
        );

        const startIndex = Math.min(lastSelectedFileIndex, fileIndex);
        const endIndex = Math.max(lastSelectedFileIndex, fileIndex);
        const rangeFileIds = availableFiles
          .slice(startIndex, endIndex + 1)
          .map((f) => f.file_id);

        // Check if all files in range are selected
        const allInRangeSelected = rangeFileIds.every((id) =>
          selectedFiles.includes(id)
        );

        setSelectedFiles((prev) => {
          if (allInRangeSelected) {
            // Deselect range
            return prev.filter((id) => !rangeFileIds.includes(id));
          } else {
            // Select range
            const newFiles = rangeFileIds.filter((id) => !prev.includes(id));
            return [...prev, ...newFiles];
          }
        });
      } else {
        // Regular click - toggle individual file selection (multiple individual selections)
        setSelectedFiles((prev) =>
          prev.includes(fileId)
            ? prev.filter((id) => id !== fileId)
            : [...prev, fileId]
        );
        setLastSelectedFileIndex(fileIndex);
      }
    },
    [groups, filteredFiles, selectedFiles, lastSelectedFileIndex]
  );

  const handleSelectAll = useCallback(() => {
    const availableFiles = filteredFiles.filter(
      (file) => !groups.some((g) => g.files.includes(file.file_id))
    );

    if (selectedFiles.length === availableFiles.length) {
      setSelectedFiles([]);
      setLastSelectedFileIndex(null);
    } else {
      setSelectedFiles(availableFiles.map((f) => f.file_id));
    }
  }, [filteredFiles, groups, selectedFiles.length]);

  const getFileById = (fileId) => {
    return allFiles.find((f) => f.file_id === fileId);
  };

  const predefinedColors = [
    "#FF9800",
    "#2196F3",
    "#4CAF50",
    "#9C27B0",
    "#FFEB3B",
    "#E91E63",
    "#607D8B",
    "#FF5722",
    "#795548",
    "#009688",
  ];

  // Given a hex color, converts to RGB, moves each channel toward 255 (white), then
  // converts back to hex and returns, used for backgrounds of group cards when a 
  // hardcoded complementary color is not present
  const lightenHexColor = (color, amount = 0.75) => {
    if (!color) {
      return
    }
    let hex = color.replace('#', '');
    if (hex.length !== 6) {
      return color;
    }

    const num = parseInt(hex, 16);
    let r = (num >> 16) & 255;
    let g = (num >> 8) & 255;
    let b = num & 255;

    // Move each channel towards 255
    r = Math.round(r + (255 - r) * amount);
    g = Math.round(g + (255 - g) * amount);
    b = Math.round(b + (255 - b) * amount);

    const toHex = (x) => x.toString(16).padStart(2, '0');

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };


  const getBackgroundColor = (color) => {
    const colorMap = {
      "#FF9800": "#FFF3E0",
      "#2196F3": "#E3F2FD",
      "#4CAF50": "#E8F5E8",
      "#9C27B0": "#F3E5F5",
      "#FFEB3B": "#FFF9C4",
      "#E91E63": "#FCE4EC",
      "#607D8B": "#ECEFF1",
      "#FF5722": "#FBE9E7",
      "#795548": "#EFEBE9",
      "#009688": "#E0F2F1",
      "#1a6d51": "#E8F5E8",
      "#dc3545": "#FDEDEE",
      "#0d6efd": "#E7F1FF",
      "#fd7e14": "#fff4eb",
      "#6f42c1": "#e8defa",
      "#20c997": "#d7fcf1",
    };
    return colorMap[color] || lightenHexColor(color, 0.75);
  };

  // Mouse interaction handlers for better UX
  const handleFileMouseEnter = useCallback((e, isAssigned, isSelected) => {
    if (!isAssigned && !isSelected) {
      e.currentTarget.style.backgroundColor = "#f9fafb";
      e.currentTarget.style.borderColor = "#d1d5db";
    }
  }, []);

  const handleFileMouseLeave = useCallback((e, isAssigned, isSelected) => {
    if (!isAssigned && !isSelected) {
      e.currentTarget.style.backgroundColor = "white";
      e.currentTarget.style.borderColor = "#e5e7eb";
    }
  }, []);

  // Get counts for display
  const availableFilesCount = filteredFiles.filter(
    (file) => !groups.some((g) => g.files.includes(file.file_id))
  ).length;

  return (
    <Card
      className="mb-4"
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "1px",
        height: "100%",
        minHeight: "400px",
      }}
    >
      <CardHeader>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <div>
            <h5
              className="mb-0"
              style={{ color: "#1a6d51", fontSize: "18px", fontWeight: "600" }}
            >
              <i className="ri-apps-2-line me-2"></i> Groups
            </h5>
          </div>

          <div className="d-flex gap-2">
            <Button
              size="sm"
              onClick={onClearAll}
              style={{
                backgroundColor: "transparent",
                border: "1px solid #dc3545",
                color: "#dc3545",
                fontSize: "12px",
                padding: "6px 12px",
                borderRadius: "6px",
              }}
            >
              <i className="ri-delete-bin-line me-1"></i>
              Clear All
            </Button>
            <Button
              onClick={() => onAutoFill(filteredFiles)}
              size="sm"
              style={{
                backgroundColor: "#28a745",
                borderColor: "#28a745",
                color: "white",
                fontSize: "12px",
                padding: "6px 12px",
                borderRadius: "6px",
              }}
            >
              <i className="ri-magic-line me-2"></i>
              Auto Fill
            </Button>
            <Button
              onClick={() => {
                // Use the common autoLabelGroups utility
                const updatedGroups = autoLabelGroups(groups, allFiles);
                updatedGroups.forEach((group) => {
                  if (
                    group.name !== groups.find((g) => g.id === group.id)?.name
                  ) {
                    onUpdateGroupName(group.id, group.name);
                  }
                });
              }}
              style={{
                backgroundColor: "#28a745",
                borderColor: "#28a745",
                color: "white",
                fontSize: "12px",
                padding: "6px 12px",
                borderRadius: "6px",
              }}
            >
              <i className="ri-price-tag-3-line me-2"></i>
              Auto Label
            </Button>
            <Button
              size="sm"
              onClick={onAddGroup}
              style={{
                backgroundColor: "#28a745",
                borderColor: "#28a745",
                color: "white",
                fontSize: "12px",
                padding: "6px 12px",
                borderRadius: "6px",
              }}
            >
              <i className="ri-add-line me-1"></i>
              Add Group
            </Button>
            <Button
              size="sm"
              color="outline-secondary"
              onClick={handleSelectAll}
              disabled={availableFilesCount === 0}
              style={{
                backgroundColor: "#28a745",
                borderColor: "#28a745",
                color: "white",
                fontSize: "12px",
                padding: "6px 12px",
                borderRadius: "6px",
              }}
            >
              <i className=" ri-arrow-right-up-fill me-2"></i>
              {selectedFiles.length === availableFilesCount &&
              availableFilesCount > 0
                ? "Deselect All"
                : "Select All"}
            </Button>
          </div>
        </div>
      </CardHeader>
      {/* Header */}
      <CardBody style={{ padding: "20px" }}>
        {/* Available Files Section with Uniform Design */}
        <div className="mb-4">
          {availableFilesCount > 0 && (
            <div
              className="text-center mb-2"
              style={{
                background:
                  "linear-gradient(135deg, #1a6d51 0%, #29a161ff 100%)",
                color: "white",
                borderRadius: "6px",
                padding: "4px 12px",
                fontSize: "11px",
                display: "inline-block",
              }}
            >
              <i
                className="ri-cursor-line me-1"
                style={{ fontSize: "12px" }}
              ></i>
              Click files to select/deselect them. Use{" "}
              <kbd
                style={{
                  fontSize: "9px",
                  padding: "1px 3px",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  border: "none",
                }}
              >
                Shift
              </kbd>
              +Click for range selection. Drag files to groups.
            </div>
          )}

          <div
            style={{
              overflowY: "auto",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              padding: "8px",
              backgroundColor: "#fafafa",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(auto-fill, minmax(${minCellWidth}px, 1fr))`,
                gap: "6px",
              }}
            >
              {filteredFiles.map((file, index) => {
                const isAssigned = groups.some((g) =>
                  g.files.includes(file.file_id)
                );
                const isSelected = selectedFiles.includes(file.file_id);

                return (
                  <div
                    key={file.file_id}
                    draggable={!isAssigned}
                    onDragStart={(e) =>
                      !isAssigned && handleDragStart(e, file.file_id)
                    }
                    onClick={(e) => handleFileClick(e, file.file_id, index)}
                    onMouseEnter={(e) =>
                      handleFileMouseEnter(e, isAssigned, isSelected)
                    }
                    onMouseLeave={(e) =>
                      handleFileMouseLeave(e, isAssigned, isSelected)
                    }
                    style={{
                      padding: "6px",
                      border: isSelected
                        ? "1px solid #2F9E37"
                        : "1px solid #e5e7eb",
                      borderRadius: "4px",
                      backgroundColor: isAssigned
                        ? "#f3f4f6"
                        : isSelected
                        ? "#f0fdf4"
                        : "white",
                      cursor: isAssigned ? "not-allowed" : "pointer",
                      fontSize: "12px",
                      opacity: isAssigned ? 0.7 : 1,
                      transition: "all 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      userSelect: "none",
                    }}
                  >
                    <Input
                      type="checkbox"
                      checked={isSelected && !isAssigned}
                      readOnly
                      style={{
                        pointerEvents: "none",
                        margin: 0,
                        width: "14px",
                        height: "14px",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "11px",
                        lineHeight: "1.2",
                        overflow: "hidden",
                        flex: 1,
                        color: isAssigned ? "#9ca3af" : "#374151",
                      }}
                      title={file.file_name}
                    >
                      {file.file_name}
                    </span>
                    {isAssigned && (
                      <Badge
                        style={{
                          fontSize: "8px",
                          backgroundColor: "#9ca3af",
                          padding: "2px 4px",
                        }}
                      >
                        Used
                      </Badge>
                    )}
                  </div>
                );
              })}

              {availableFilesCount === 0 && (
                <div
                  className="text-muted text-center"
                  style={{
                    gridColumn: "1 / -1",
                    padding: "20px",
                    fontSize: "14px",
                  }}
                >
                  All files have been assigned to groups
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Groups Grid */}
        <Row>
          {groups.map((group, index) => (
            <Col key={group.id} xl={4} lg={6} md={6} className="mb-3">
              <Card
                key={group.id}
                style={{
                  backgroundColor: getBackgroundColor(group.color),
                  border:
                    dragOverGroup === group.id
                      ? `2px dashed ${group.color}`
                      : `1px solid ${group.color}`,
                  borderRadius: "8px",
                  minHeight: "120px",
                  transition: "all 0.2s ease",
                  position: "relative", // Important for tooltip positioning
                  transform:
                    dragOverGroup === group.id ? "scale(1.02)" : "scale(1)",
                  boxShadow:
                    dragOverGroup === group.id
                      ? `0 4px 12px rgba(0,0,0,0.15), 0 0 0 4px ${group.color}20`
                      : "none",
                  opacity:
                    draggedFileSourceGroup === group.id && !dragOverGroup
                      ? 0.7
                      : 1,
                }}
                onDragOver={(e) => handleDragOver(e, group.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, group.id)}
              >
                {/* Tooltip - now only shows when hovering the info icon */}
                {tooltipVisible[group.id] && (
                  <div
                    style={{
                      position: "absolute",
                      top: "8px",
                      left: "8px",
                      backgroundColor: "#333",
                      color: "white",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      fontSize: "11px",
                      lineHeight: "1.3",
                      zIndex: 1000,
                      maxWidth: "200px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                      pointerEvents: "none",
                    }}
                  >
                    💡 Not limited to 6 colors! Use the color circle to choose
                    any RGB color for your group.
                    {/* Small arrow pointing down-right */}
                    <div
                      style={{
                        position: "absolute",
                        bottom: "-6px",
                        left: "12px",
                        width: "0",
                        height: "0",
                        borderLeft: "6px solid transparent",
                        borderRight: "6px solid transparent",
                        borderTop: "6px solid #333",
                      }}
                    />
                  </div>
                )}

                <CardBody style={{ padding: "20px" }}>
                  {/* Group Header */}
                  <div className="d-flex justify-content-between align-items-center mb-3 w-100">
                    <div className="d-flex align-items-center flex-grow-1">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginRight: "4px",
                          cursor: "help",
                        }}
                        onMouseEnter={() => handleTooltipShow(group.id)}
                        onMouseLeave={() => handleTooltipHide(group.id)}
                      >
                        <RiInformationLine
                          style={{
                            width: "18px",
                            height: "18px",
                            color: "#6b7280",
                            cursor: "help",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = "#4b5563";
                            e.target.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = "#6b7280";
                            e.target.style.transform = "scale(1)";
                          }}
                        />
                      </div>

                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          borderRadius: "50%",
                          backgroundColor: group.color,
                          marginRight: "8px",
                          cursor: "pointer",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <input
                          type="color"
                          value={group.color}
                          onChange={(e) =>
                            onUpdateGroupColor(group.id, e.target.value)
                          }
                          style={{
                            position: "absolute",
                            top: "-2px",
                            left: "-2px",
                            width: "20px",
                            height: "20px",
                            border: "none",
                            cursor: "pointer",
                            opacity: 0, // Hide the actual input
                          }}
                        />
                      </div>
                      <Input
                        type="text"
                        value={group.name}
                        onChange={(e) =>
                          onUpdateGroupName(group.id, e.target.value)
                        }
                        style={{
                          // border: "none",
                          // backgroundColor: "transparent",
                          fontWeight: "600",
                          fontSize: "13px",
                          padding: "4px",
                          color: "#333",
                          flex: 1,
                          width: "100%",
                          maxWidth: "none",
                          boxShadow: "none",
                        }}
                        placeholder={`Group ${index + 1}`}
                      />
                    </div>

                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    {/* Quick Color Options */}
                    <div className="d-flex gap-1">
                        {predefinedColors.slice(0, 6).map((color) => (
                          <div
                            key={color}
                            onClick={() => onUpdateGroupColor(group.id, color)}
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: color,
                              borderRadius: "50%",
                              cursor: "pointer",
                              border:
                                group.color === color
                                  ? "2px solid #333"
                                  : "1px solid #ddd",
                              transition: "all 0.1s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = "scale(1)";
                            }}
                          />
                        ))}
                      </div>

                      <div className="d-flex gap-1">
                        {/* Clear Group Button */}
                        <Button
                          size="sm"
                          onClick={() => onClearGroup(group.id)}
                          style={{
                            backgroundColor: "transparent",
                            border: "1px solid #f59e0b",
                            color: "#f59e0b",
                            fontSize: "10px",
                            padding: "4px 8px",
                            fontWeight: "500",
                            borderRadius: "4px",
                          }}
                        >
                          <i className="ri-delete-bin-line me-1"></i>
                          Clear
                        </Button>
                        {/* Delete Group Button */}
                        <Button
                          size="sm"
                          onClick={() => onRemoveGroup(group.id)}
                          style={{
                            backgroundColor: "transparent",
                            border: "1px solid #fc0303",
                            color: "#fc0303",
                            padding: "4px 8px",
                            fontSize: "10px",
                            borderRadius: "4px",
                          }}
                        >
                          <i className="ri-delete-bin-line"></i>
                          Delete
                        </Button>
                      </div>
                  </div>

                  {/* Files or Drop Zone */}
                  {group.files.length === 0 ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "100px",
                        border: "2px dashed #ccc",
                        borderRadius: "8px",
                        backgroundColor: "rgba(255,255,255,0.5)",
                        color: "#999",
                        fontSize: "12px",
                        textAlign: "center",
                      }}
                    >
                      <i
                        className="ri-upload-cloud-2-line"
                        style={{ fontSize: "24px", marginBottom: "8px" }}
                      ></i>
                      <div>Drag files here</div>
                      <div style={{ fontSize: "10px", marginTop: "4px" }}>
                        {selectedFiles.length > 0
                          ? `${selectedFiles.length} files selected`
                          : "Select files to drag"}
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop: "8px" }}>
                      <div
                        style={{
                          minHeight: "100px",
                          overflowY: "auto",
                        }}
                      >
                        {group.files.map((fileId, fileIndex) => {
                          const file = getFileById(fileId);
                          const isDragging =
                            draggedFile && draggedFile.includes(fileId);
                          return file ? (
                            <div
                              key={fileId}
                              className="d-flex justify-content-between align-items-center mb-2"
                              style={{
                                padding: "2px 12px",
                                backgroundColor: isDragging
                                  ? "rgba(255,255,255,0.5)"
                                  : "rgba(255,255,255,0.8)",
                                borderRadius: "6px",
                                fontSize: "12px",
                                border: "1px solid rgba(255,255,255,0.5)",
                                cursor: "grab",
                                transition: "all 0.2s ease",
                                opacity: isDragging ? 0.5 : 1,
                                transform: isDragging
                                  ? "scale(0.95)"
                                  : "scale(1)",
                              }}
                              draggable={true}
                              onDragStart={(e) =>
                                handleGroupFileDragStart(e, fileId, group.id)
                              }
                              onDragEnd={handleDragEnd}
                              onMouseEnter={(e) => {
                                if (!isDragging) {
                                  e.target.style.cursor = "grabbing";
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.cursor = "grab";
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: "500",
                                  color: "#333",
                                  overflow: "hidden",
                                  flex: 1,
                                }}
                              >
                                {file.file_name}
                              </span>
                              <Button
                                size="sm"
                                onClick={() =>
                                  onRemoveFilesFromGroup(group.id, [fileId])
                                }
                                style={{
                                  backgroundColor: "transparent",
                                  border: "none",
                                  color: "#dc3545",
                                  padding: "2px 4px",
                                  fontSize: "10px",
                                  marginLeft: "8px",
                                }}
                              >
                                <i className="ri-close-line"></i>
                              </Button>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </CardBody>
    </Card>
  );
};

export default GroupsManager;
